import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { BuildingNode } from '../add-object-tree/add-object-tree.component';

export class BuildingFlatNode {
  buildingTypeName?: string;
  buildingName?: string;
  buildingTypeId?: string;
  buildingType?: string;
  address?: string;
  guid?: string;
  efficiencyAnalysisDate?: string;
  efficiencyPercentageElectricity?: string;
  city?: number;
  efficiencyPercentageHeat?: number;
  electricityColorStatus?: string;
  electricityComputedValue?: number;
  heatColorStatus?: string;
  heatComputedValue?: number;
  street?: string;
  level?: number;
  expandable?: boolean;
}

@Component({
  selector: 'app-building-type-filter',
  templateUrl: './building-type-filter.component.html',
  styleUrls: ['./building-type-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BuildingTypeFilterComponent implements OnInit {
  @Input() reset: Subject<boolean> = new Subject<boolean>();
  @Output() updateBuildingWithEa = new EventEmitter();
  @Output() updateSelectedText = new EventEmitter();

  public isAllSelected = false;
  public flatNodeMap = new Map<BuildingFlatNode, BuildingNode>();
  nestedNodeMap = new Map<BuildingNode, BuildingFlatNode>();
  public treeControl: FlatTreeControl<BuildingFlatNode>;
  public treeFlattener: MatTreeFlattener<BuildingNode, BuildingFlatNode>;
  public dataSource: MatTreeFlatDataSource<BuildingNode, BuildingFlatNode>;
  public checklistSelection = new SelectionModel<BuildingFlatNode>(true);
  public dataSourceChange: BehaviorSubject<BuildingNode[]> = new BehaviorSubject<BuildingNode[]>([]);

  constructor(private _efficiencyAnalysisService: EfficiencyAnalysisService) {
    const buildingsWithEAFromService = this._efficiencyAnalysisService.buildingsWithEA as any[];

    this.treeFlattener = new MatTreeFlattener(this._transformer, this._getLevel, this._isExpandable, this._getChildren);

    this.treeControl = new FlatTreeControl<BuildingFlatNode>(this._getLevel, this._isExpandable);

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.dataSourceChange.subscribe((data: BuildingNode[]) => {
      this.dataSource.data = data;
    });

    this.dataSourceChange.next(buildingsWithEAFromService);
  }

  ngOnInit() {
    this.reset.subscribe(() => {
      this.checklistSelection.clear();
      this.dataSourceChange.next(this._efficiencyAnalysisService.buildingsWithEA as any[]);
      this.toggleSelect(false);
    });
  }

  private _getLevel(node: BuildingFlatNode): number {
    return node.level as number;
  }

  private _isExpandable(node: BuildingFlatNode): boolean {
    return node.expandable as boolean;
  }

  private _getChildren(node: BuildingNode): BuildingNode[] {
    return node.children as BuildingNode[];
  }

  public hasChild(_: number, _nodeData: BuildingFlatNode): boolean {
    return _nodeData.expandable as boolean;
  }

  private _transformer = (node: BuildingNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.buildingTypeName === node.buildingTypeName ? existingNode : new BuildingFlatNode();
    flatNode.buildingTypeName = node.buildingTypeName;
    flatNode.buildingName = node.buildingName;
    flatNode.address = node.address;
    flatNode.guid = node.guid;
    flatNode.level = level;
    flatNode.expandable = !!node.children;
    flatNode.buildingTypeId = node.buildingTypeId;
    flatNode.efficiencyAnalysisDate = node.efficiencyAnalysisDate;
    flatNode.efficiencyPercentageElectricity = node.efficiencyPercentageElectricity;
    flatNode.city = node.city;
    flatNode.efficiencyPercentageHeat = node.efficiencyPercentageHeat;
    flatNode.electricityColorStatus = node.electricityColorStatus;
    flatNode.electricityComputedValue = node.electricityComputedValue;
    flatNode.heatColorStatus = node.heatColorStatus;
    flatNode.heatComputedValue = node.heatComputedValue;
    flatNode.buildingType = node.buildingType;
    flatNode.street = node.street;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  public descendantsAllSelected(node: BuildingFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return (
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      })
    );
  }

  public descendantsPartiallySelected(node: BuildingFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  public buildingItemSelectionToggle(node: BuildingFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

    descendants.forEach((child) => this.checklistSelection.isSelected(child));
    this._checkAllParentsSelection(node);
    this.updateSelection();
  }

  public buildingLeafItemSelectionToggle(node: BuildingFlatNode): void {
    this.checklistSelection.toggle(node);
    this._checkAllParentsSelection(node);
    this.updateSelection();
  }

  private _checkAllParentsSelection(node: BuildingFlatNode): void {
    let parent: BuildingFlatNode | null = this._getParentNode(node);
    while (parent) {
      this._checkRootNodeSelection(parent);
      parent = this._getParentNode(parent);
    }

    this._checkParentNodeIsAllSelected();
  }

  private _checkParentNodeIsAllSelected(): void {
    const flatDataSource = this.treeControl.dataNodes;
    const descFlatData = flatDataSource.filter((f) => f.level === 0);

    const descSelected = descFlatData
      .map((v) => {
        return this.checklistSelection.isSelected(v);
      })
      .filter((v) => v === false);

    const selectedText = flatDataSource
      .map((f) => {
        const selected = this.checklistSelection.isSelected(f);
        if (selected) {
          if (f.level === 0) {
            return f.buildingTypeName;
          } else {
            return f.buildingName;
          }
        }
      })
      .filter((f) => f !== undefined) as string[];

    this._setSelectedText(selectedText);

    if (descSelected.length === 0) {
      this.isAllSelected = true;
    } else {
      this.isAllSelected = false;
    }
  }

  private _setSelectedText(selectedText: string[]): void {
    if (selectedText.length > 0) {
      this.updateSelectedText.emit(selectedText.join(', '));
    } else {
      this.updateSelectedText.emit();
    }
  }

  private _checkRootNodeSelection(node: BuildingFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });

    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  private _getParentNode(node: BuildingFlatNode): BuildingFlatNode | null {
    const currentLevel = this._getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this._getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  public hasChildren(_: number, node: BuildingNode) {
    return !!node.children && node.children.length > 0;
  }

  public toggleSelect(isCheck: boolean): void {
    const flatSourceData = this.treeControl.dataNodes;

    if (isCheck) {
      flatSourceData.forEach((node) => {
        this.checklistSelection.select(node);
      });
    } else {
      flatSourceData.forEach((node) => {
        this.checklistSelection.deselect(node);
      });
    }

    this.updateSelection();
    this._checkParentNodeIsAllSelected();
  }

  public updateSelection(): void {
    const selected = this.checklistSelection.selected;
    const filteredBuildingByLevel = selected.filter((v: BuildingFlatNode) => v.level && v.level > 0);
    const hasSelectedBuildings = selected !== undefined && selected.length > 0;

    if (hasSelectedBuildings) {
      this.updateBuildingWithEa.emit(filteredBuildingByLevel);
    } else {
      this.updateBuildingWithEa.emit([]);
    }
  }
}
